import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
} from "react-router-dom";
import {
  User,
  Users,
  UserPersonalDetails,
  UserTrainingProgram,
} from "../screens/users";
import Login from "../screens/login/Login";
import SideBar from "./sideBar/SideBar";
import {
  TrainerDetails,
  TrainingPrograms,
  TrainingProgramsTable,
  Exercises,
  SingleExercise,
  Trainers,
  TrainerPersonalDetails,
  TechniqueVideos,
  ExercisesTable,
  TrainingProgramWorkoutsTable,
  TrainingProgramDetails,
  TrainingProgram,
  TrainingProgramWorkouts,
  Workouts,
  WorkoutsTable,
  Workout,
  WorkoutDetails,
  WorkoutContent,
  TechniqueVideosTable,
  TechniqueVideoDetails,
} from "../screens/trainers";
import {
  ScreeenLayout,
  AppContent,
  AppLayout,
  FullHeightSpin,
  StyledBigLasuLogo,
  StyledLogoText,
} from "../app/style";
import { routes } from "./routes";
import { useAppSelector, useAppDispatch } from "../store/config";
import { useEffect } from "react";
import {
  authCheckStart,
  authLogoutStart,
} from "../store/features/auth/auth-slice";
import {
  Diet,
  Recipe,
  RecipeDetails,
  RecipeInstructions,
  RecipesTable,
} from "../screens/diet";
import { Dashboard } from "../screens/dashboard/Dashboard";
import { Spin } from "antd";

export const RootNavigation = () => {
  const { authenticated, error, authCheck, user } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(authCheckStart());
  }, [dispatch]);

  if (authCheck) {
    return (
      <FullHeightSpin>
        <StyledLogoText>
          <StyledBigLasuLogo />
          Lasu
        </StyledLogoText>
        <Spin size="large" />
      </FullHeightSpin>
    );
  }

  const router = createBrowserRouter([
    ...(authenticated
      ? []
      : [{ path: routes.appRoutes.login, element: <Login /> }]),
    {
      path: "/",
      element: <AuthGuard authenticated={authenticated} />,
      children: [
        {
          path: "",
          element: (
            <Navigate
              to={
                user?.role === "admin"
                  ? "/users"
                  : user?.role === "trainer"
                  ? "/dashboard"
                  : "/login"
              }
              replace
            />
          ),
        },
        {
          path: routes.appRoutes.users,
          element: (
            <RoleGuard isPermitted={user?.role === "admin"}>
              <Users />
            </RoleGuard>
          ),
          children: [
            {
              path: ":id",
              element: <User />,
              children: [
                { path: "", element: <UserPersonalDetails /> },
                {
                  path: "training-programs",
                  element: <UserTrainingProgram type="active" key="active" />,
                },
                {
                  path: "training-programs-history",
                  element: (
                    <UserTrainingProgram type="finished" key="finished" />
                  ),
                },
                {
                  path: "training-programs-unfinished",
                  element: (
                    <UserTrainingProgram type="unfinished" key="unfinished" />
                  ),
                },
              ],
            },
          ],
        },
        {
          path: routes.appRoutes.trainers,
          element: (
            <RoleGuard isPermitted={user?.role === "admin"}>
              <Trainers />
            </RoleGuard>
          ),
          children: [
            {
              path: ":id",
              element: <TrainerDetails />,
              children: [
                { path: "", element: <TrainerPersonalDetails /> },
                {
                  path: "exercises",
                  element: <Exercises />,
                  children: [
                    { path: "", element: <ExercisesTable /> },
                    { path: ":exerciseId", element: <SingleExercise /> },
                  ],
                },
                {
                  path: "training-programs",
                  element: <TrainingPrograms />,
                  children: [
                    { path: "", element: <TrainingProgramsTable /> },
                    {
                      path: ":trainingProgramId",
                      element: <TrainingProgram />,
                      children: [
                        { path: "", element: <TrainingProgramDetails /> },
                        {
                          path: "workouts",
                          element: <TrainingProgramWorkouts />,
                          children: [
                            {
                              path: "",
                              element: <TrainingProgramWorkoutsTable />,
                            },
                            {
                              path: ":workoutId",
                              element: <Workout />,
                              children: [
                                { path: "", element: <WorkoutDetails /> },
                                {
                                  path: "workout-content",
                                  element: <WorkoutContent />,
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },

                {
                  path: "workouts",
                  element: <Workouts />,
                  children: [
                    {
                      path: "",
                      element: <WorkoutsTable />,
                    },
                    {
                      path: ":workoutId",
                      element: <Workout />,
                      children: [
                        {
                          path: "",
                          element: <WorkoutDetails />,
                        },
                        {
                          path: "workout-content",
                          element: <WorkoutContent />,
                        },
                      ],
                    },
                  ],
                },

                {
                  path: "technique-videos",
                  element: <TechniqueVideos />,
                  children: [
                    {
                      path: "",
                      element: <TechniqueVideosTable />,
                    },
                    {
                      path: ":vodId",
                      element: <TechniqueVideoDetails />,
                    },
                  ],
                },

                { path: "dashboard", element: <Dashboard isAdmin={true} /> },
              ],
            },
          ],
        },
        {
          path: routes.appRoutes.diet,
          element: (
            <RoleGuard isPermitted={user?.role === "admin"}>
              <Diet />
            </RoleGuard>
          ),
          children: [
            { path: "", element: <RecipesTable /> },
            {
              path: ":recipeId",
              element: <Recipe />,
              children: [
                { path: "", element: <RecipeDetails /> },
                {
                  path: "recipe-instructions",
                  element: <RecipeInstructions />,
                },
              ],
            },
          ],
        },

        ...(user?.role === "trainer"
          ? [
              {
                path: routes.appRoutes.dashboard,
                element: <Dashboard isAdmin={false} />,
              },
            ]
          : []),

        {
          path: "*",
          element: (
            <Navigate
              to={
                user?.role === "admin"
                  ? "/users"
                  : user?.role === "trainer"
                  ? "/dashboard"
                  : "/login"
              }
              replace
            />
          ),
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

const AuthGuard = ({ authenticated }: { authenticated: boolean }) => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  const validRoles = ["admin", "trainer"];
  if (!validRoles.includes(user?.role)) {
    dispatch(authLogoutStart());
    return <Navigate to="/login" replace />;
  }

  return (
    <AppLayout>
      <SideBar />
      <ScreeenLayout>
        <AppContent>
          <Outlet />
        </AppContent>
      </ScreeenLayout>
    </AppLayout>
  );
};

interface RoleGuardProps {
  isPermitted: boolean;
  children: React.ReactNode;
}

const RoleGuard = ({ isPermitted, children }: RoleGuardProps) => {
  if (!isPermitted) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
